import { useContext, useMemo } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import StudentDashboardShell from '../components/studentDashboard/StudentDashboardShell';
import PerformanceTrendChart from '../components/studentDashboard/PerformanceTrendChart';
import StudentProfileEditCard from '../components/studentDashboard/StudentProfileEditCard';
import { useStudentDashboardData } from '../hooks/useStudentDashboardData';
import {
  formatDisplayDate,
  countCompletedModules
} from '../utils/studentDashboardUtils';

const cardClass =
  'rounded-xl border p-5 shadow-sm theme-animate';
const cardStyle = {
  backgroundColor: 'var(--color-surface)',
  borderColor: 'var(--color-border)'
};

const statCard = `${cardClass} flex flex-col gap-1`;

const StudentDashboardPage = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const token = user?.token;
  const authUser = user?.user || user;
  const email = authUser?.email || '';
  const role = authUser?.role || user?.role;
  const displayNameFromAuth = authUser?.name || '';

  const {
    student,
    loading,
    error,
    displayName,
    stats,
    primaryEnrollment,
    trend,
    placementRows,
    courseCards,
    reload
  } = useStudentDashboardData(email);

  const name = displayName || displayNameFromAuth || 'Student';
  const avatarLetter = (name || '?').trim().charAt(0).toUpperCase();

  const completedModules = useMemo(() => countCompletedModules(student), [student]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (!token) {
    return <Navigate to="/" replace />;
  }

  if (role && role !== 'student') {
    return <Navigate to="/dashboard" replace />;
  }

  const batch = primaryEnrollment?.batch;
  const course = batch?.course;

  return (
    <StudentDashboardShell
      displayName={name}
      email={email}
      avatarLetter={avatarLetter}
      onLogout={handleLogout}
    >
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3 theme-text-secondary">
          <div
            className="h-10 w-10 rounded-full border-2 border-t-transparent animate-spin"
            style={{ borderColor: 'var(--color-primary)' }}
          />
          <p>Loading your dashboard…</p>
        </div>
      ) : error === 'not_found' || !student ? (
        <div className={`${cardClass} max-w-lg`} style={cardStyle}>
          <h2 className="text-xl font-bold theme-text-gradient mb-2">Welcome</h2>
          <p className="theme-text-secondary mb-4">
            We couldn&apos;t find a learner profile linked to <strong>{email}</strong>. Your admin may still
            be setting up your record, or your login email may differ from the email on file.
          </p>
          <button
            type="button"
            onClick={() => reload()}
            className="px-4 py-2 rounded-lg text-white text-sm font-medium theme-focus"
            style={{ backgroundColor: 'var(--color-primary)' }}
          >
            Retry
          </button>
        </div>
      ) : error ? (
        <div className={`${cardClass}`} style={{ ...cardStyle, borderColor: 'var(--color-error)' }}>
          <p className="text-red-600">{error}</p>
          <button type="button" onClick={() => reload()} className="mt-3 text-sm underline">
            Try again
          </button>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Profile card */}
          <section id="sd-profile" className={cardClass} style={cardStyle}>
            <h2 className="text-lg font-bold theme-text-gradient mb-4 flex items-center gap-2">
              <span>👤</span> Profile
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="theme-text-secondary text-xs uppercase tracking-wide">Name</p>
                <p className="font-semibold" style={{ color: 'var(--color-text)' }}>
                  {name}
                </p>
              </div>
              <div>
                <p className="theme-text-secondary text-xs uppercase tracking-wide">Email</p>
                <p className="font-semibold" style={{ color: 'var(--color-text)' }}>
                  {student.emailAddress || email}
                </p>
              </div>
              <div>
                <p className="theme-text-secondary text-xs uppercase tracking-wide">Role</p>
                <p className="font-semibold capitalize" style={{ color: 'var(--color-text)' }}>
                  {role || 'student'}
                </p>
              </div>
              <div>
                <p className="theme-text-secondary text-xs uppercase tracking-wide">Joined</p>
                <p className="font-semibold" style={{ color: 'var(--color-text)' }}>
                  {formatDisplayDate(student.createdAt)}
                </p>
              </div>
            </div>
          </section>

          <StudentProfileEditCard student={student} onSaved={reload} />

          {/* Stats */}
          <section id="sd-stats" className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className={statCard} style={cardStyle}>
              <span className="text-xs font-medium theme-text-secondary">Enrolled courses</span>
              <span className="text-2xl font-bold theme-text-gradient">{stats.enrolledCourses}</span>
            </div>
            <div className={statCard} style={cardStyle}>
              <span className="text-xs font-medium theme-text-secondary">Assignments due</span>
              <span className="text-2xl font-bold theme-text-gradient">{stats.assignmentsDue}</span>
              <span className="text-xs theme-text-secondary">Resume / readiness deadlines</span>
            </div>
            <div className={statCard} style={cardStyle}>
              <span className="text-xs font-medium theme-text-secondary">Avg score</span>
              <span className="text-2xl font-bold theme-text-gradient">
                {stats.avgScore != null ? `${stats.avgScore} / 5` : '—'}
              </span>
              <span className="text-xs theme-text-secondary">From readiness rubric</span>
            </div>
            <div className={statCard} style={cardStyle}>
              <span className="text-xs font-medium theme-text-secondary">Completion</span>
              <span className="text-2xl font-bold theme-text-gradient">
                {stats.completionPct != null ? `${stats.completionPct}%` : '—'}
              </span>
              <span className="text-xs theme-text-secondary">Avg module progress</span>
            </div>
          </section>

          {/* Batch */}
          <section id="sd-batch" className={cardClass} style={cardStyle}>
            <h2 className="text-lg font-bold theme-text-gradient mb-4">📦 Batch information</h2>
            {batch ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                <Field label="Batch name" value={batch.batchName} />
                <Field label="Batch code" value={batch.batchCode || batch.batchNumber || '—'} />
                <Field label="Trainer" value="—" hint="Assigned by your institute" />
                <Field label="Start date" value={formatDisplayDate(batch.startDate)} />
                <Field
                  label="End date"
                  value={formatDisplayDate(batch.endDate || batch.tentativeEndDate)}
                />
                <Field
                  label="Schedule"
                  value={[batch.status, batch.mode, batch.location].filter(Boolean).join(' · ') || '—'}
                />
                <Field label="Mode" value={batch.mode} />
                <Field label="Course" value={course?.courseName || '—'} />
              </div>
            ) : (
              <p className="theme-text-secondary">You are not enrolled in a batch yet.</p>
            )}
          </section>

          {/* Courses */}
          <section id="sd-courses" className={cardClass} style={cardStyle}>
            <h2 className="text-lg font-bold theme-text-gradient mb-4">📚 Courses linked to your batches</h2>
            {courseCards.length === 0 ? (
              <p className="theme-text-secondary">No course enrollments to show.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {courseCards.map((c) => (
                  <div
                    key={c.key}
                    className="rounded-lg border p-4 flex flex-col gap-3"
                    style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
                  >
                    <div className="flex justify-between gap-2 items-start">
                      <div>
                        <h3 className="font-semibold" style={{ color: 'var(--color-text)' }}>
                          {c.courseName}
                        </h3>
                        <p className="text-xs theme-text-secondary">{c.batchName}</p>
                      </div>
                      <span
                        className="text-xs px-2 py-1 rounded-full font-medium shrink-0"
                        style={{
                          backgroundColor: 'var(--color-surface-elevated)',
                          color: 'var(--color-text)'
                        }}
                      >
                        {c.status}
                      </span>
                    </div>
                    <div className="text-sm space-y-1 theme-text-secondary">
                      <p>
                        Trainer: <span style={{ color: 'var(--color-text)' }}>{c.trainer}</span>
                      </p>
                      <p>
                        Duration:{' '}
                        <span style={{ color: 'var(--color-text)' }}>
                          {c.durationDays != null ? `${c.durationDays} days` : '—'}
                        </span>
                      </p>
                    </div>
                    <div>
                      <div className="flex justify-between text-xs mb-1 theme-text-secondary">
                        <span>Progress</span>
                        <span>{c.progress}%</span>
                      </div>
                      <div
                        className="h-2 rounded-full overflow-hidden"
                        style={{ backgroundColor: 'var(--color-border)' }}
                      >
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: `${Math.min(100, c.progress)}%`,
                            backgroundColor: 'var(--color-primary)'
                          }}
                        />
                      </div>
                    </div>
                    <button
                      type="button"
                      className="mt-auto w-full py-2.5 rounded-lg text-sm font-medium text-white theme-focus"
                      style={{ backgroundColor: 'var(--color-primary)' }}
                    >
                      Continue
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Assignments placeholder */}
          <section id="sd-assignments" className={cardClass} style={cardStyle}>
            <h2 className="text-lg font-bold theme-text-gradient mb-2">📝 Assignments</h2>
            <p className="text-sm theme-text-secondary mb-4">
              Assignment tracking will appear here when linked to your batch LMS. For now, upcoming readiness
              deadlines are reflected in <strong>Assignments due</strong> above.
            </p>
            <ul className="text-sm space-y-2 theme-text-secondary list-disc list-inside">
              <li>Submit resume / portfolio when your trainer marks the gate open.</li>
              <li>Complete module labs in order — progress updates your course cards.</li>
            </ul>
          </section>

          {/* Performance */}
          <section id="sd-performance" className={cardClass} style={cardStyle}>
            <h2 className="text-lg font-bold theme-text-gradient mb-4">📊 Performance</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h3 className="text-sm font-semibold mb-2 theme-text-secondary">Module completion trend</h3>
                <PerformanceTrendChart points={trend} height={140} />
              </div>
              <div className="space-y-4">
                <div
                  className="rounded-lg p-4 border"
                  style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
                >
                  <p className="text-xs theme-text-secondary uppercase">Avg score (readiness)</p>
                  <p className="text-2xl font-bold theme-text-gradient">
                    {stats.avgScore != null ? `${stats.avgScore} / 5` : '—'}
                  </p>
                </div>
                <div
                  className="rounded-lg p-4 border"
                  style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-background)' }}
                >
                  <p className="text-xs theme-text-secondary uppercase">Completed modules</p>
                  <p className="text-2xl font-bold theme-text-gradient">{completedModules}</p>
                  <p className="text-xs theme-text-secondary mt-1">Across all current enrollments</p>
                </div>
              </div>
            </div>
          </section>

          {/* Projects */}
          <section id="sd-projects" className={cardClass} style={cardStyle}>
            <h2 className="text-lg font-bold theme-text-gradient mb-4">💻 Projects</h2>
            <p className="text-sm theme-text-secondary mb-4">
              Capstone and minor/major projects will list here when recorded on your profile.
            </p>
            <div
              className="rounded-lg border border-dashed p-6 text-center text-sm theme-text-secondary"
              style={{ borderColor: 'var(--color-border)' }}
            >
              <p className="mb-2">No projects on file yet.</p>
              <p className="text-xs">
                Typical fields: title, technologies, completion date, GitHub link — your coordinator can add
                these to your learner record.
              </p>
            </div>
          </section>

          {/* Placement */}
          <section id="sd-placement" className={cardClass} style={cardStyle}>
            <h2 className="text-lg font-bold theme-text-gradient mb-4">🏢 Placement</h2>
            {placementRows.length === 0 ? (
              <p className="theme-text-secondary text-sm">
                No placement activity recorded yet. Opportunities and offers will show here when added by
                placement ops.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left theme-text-secondary border-b" style={{ borderColor: 'var(--color-border)' }}>
                      <th className="py-2 pr-4">Company</th>
                      <th className="py-2 pr-4">Interview / date</th>
                      <th className="py-2 pr-4">Result</th>
                      <th className="py-2 pr-4">Offer status</th>
                      <th className="py-2">Batch</th>
                    </tr>
                  </thead>
                  <tbody>
                    {placementRows.map((row) => (
                      <tr key={row.key} className="border-b" style={{ borderColor: 'var(--color-border)' }}>
                        <td className="py-2 pr-4 font-medium" style={{ color: 'var(--color-text)' }}>
                          {row.company}
                        </td>
                        <td className="py-2 pr-4">{formatDisplayDate(row.interviewDate)}</td>
                        <td className="py-2 pr-4">{row.result}</td>
                        <td className="py-2 pr-4">{row.offerStatus}</td>
                        <td className="py-2 text-xs theme-text-secondary">{row.batchName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </StudentDashboardShell>
  );
};

function Field({ label, value, hint }) {
  return (
    <div>
      <p className="theme-text-secondary text-xs uppercase tracking-wide">{label}</p>
      <p className="font-medium" style={{ color: 'var(--color-text)' }}>
        {value || '—'}
      </p>
      {hint ? <p className="text-xs theme-text-secondary mt-0.5">{hint}</p> : null}
    </div>
  );
}

export default StudentDashboardPage;
